import { SchoolOutlined as SchoolOutlinedIcon, SchoolRounded as SchoolRoundedIcon } from '@mui/icons-material';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useContext, useState } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { EducationHistoryItem } from '~/shared/data-contract/education-history';

import { CardIconCaption } from '../../../../design/components/cards/CardIconCaption';
import { InlineLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { useEducationHistoryQuery } from '../../../query-hooks/users/education-history';
import { useUserHistoryOptionsQuery } from '../../../query-hooks/users/user-history';
import { useScrollToLocationHash } from '../../../use-scroll-to-location-hash';
import { createUserEducationHistoryUrl, editUserEducationHistoryUrl } from '../constants';
import { AddButton } from '../user-profile/common/ActionButtons';
import { CardPlaceholder } from './common/CardPlaceholder';
import { HistoryItem } from './common/HistoryItem';
import { HistoryItemListWrapper } from './common/HistoryItemListWrapper';
import { ShowAllButton } from './common/ShowAllButton';

const EducationHistoryCaption: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <CardIconCaption
            color="education.main"
            icon={SchoolOutlinedIcon}
            text={translate('pages.user-education-history.caption')}
        />
    );
};

type EducationHistoryContentProps = {
    readonly canUserAddEducationHistory?: boolean;
    readonly canUserEditEducationHistory?: boolean;
    readonly educationHistory?: ReadonlyArray<EducationHistoryItem>;
    readonly userId?: string;
};

export const EducationHistoryContent: FunctionComponent<EducationHistoryContentProps> = ({
    canUserAddEducationHistory = false,
    canUserEditEducationHistory = false,
    educationHistory = [],
    userId,
}) => {
    const { actions, translate } = useContext(ApplicationContext);
    const [showAll, setShowAll] = useState(false);
    const hasEducationHistory = educationHistory.length > 0;
    const slicedEducations = showAll ? educationHistory : educationHistory.slice(0, 2);

    const headlineActions = canUserAddEducationHistory && is.nonEmptyString(userId) && (
        <AddButton onClick={() => actions.navigateTo(createUserEducationHistoryUrl(userId))} />
    );

    const editAction = (itemId: string): VoidFunction | undefined =>
        canUserEditEducationHistory && is.nonEmptyString(userId)
            ? () => actions.navigateTo(editUserEducationHistoryUrl(userId, itemId))
            : undefined;

    useScrollToLocationHash();

    return (
        <HistoryItemListWrapper
            anchorId="education-history"
            headline={translate('pages.recruiter-profile.background.education-history.title')}
            headlineActions={headlineActions}
        >
            {hasEducationHistory ? (
                slicedEducations.map((educationItem) => (
                    <HistoryItem
                        key={educationItem.id}
                        caption={<EducationHistoryCaption />}
                        dateFrom={educationItem.dateFrom}
                        dateTo={educationItem.dateTo}
                        description={educationItem.description}
                        skills={educationItem.skills}
                        skillsHeadline={translate('pages.user-education-history.skills-list-headline')}
                        subTitle={[educationItem.schoolName]}
                        title={`${educationItem.degree}, ${educationItem.fieldOfStudy}`}
                        onClickEdit={editAction(educationItem.id)}
                    />
                ))
            ) : (
                <CardPlaceholder
                    placeholderIcon={<SchoolRoundedIcon />}
                    placeholderText={translate('pages.recruiter-profile.background.education-history.placeholder')}
                />
            )}
            {educationHistory.length > 2 && <ShowAllButton showAll={showAll} onClick={() => setShowAll(!showAll)} />}
        </HistoryItemListWrapper>
    );
};

type EducationHistoryProps = {
    readonly userId: string;
};

export const EducationHistory: FunctionComponent<EducationHistoryProps> = ({ userId }: EducationHistoryProps) => {
    const { data: userHistoryOptions, isFetching: userHistoryOptionsIsFetching } = useUserHistoryOptionsQuery(userId);
    const { data: educationHistory, isFetching: educationHistoryIsFetching } = useEducationHistoryQuery(
        userId,
        userHistoryOptions?.canReadHistory ?? false,
    );

    if (userHistoryOptionsIsFetching || educationHistoryIsFetching) {
        return <InlineLoadingSpinner />;
    }

    return (
        <EducationHistoryContent
            canUserAddEducationHistory={userHistoryOptions?.canEditHistory}
            canUserEditEducationHistory={userHistoryOptions?.canEditHistory}
            educationHistory={educationHistory}
            userId={userId}
        />
    );
};
